/**
 * What a handle may be, in the one place both sides read.
 *
 * Pure and dependency free, for the same reason `scoring.ts` is: the page that
 * offers a handle and the server that accepts one must never disagree about
 * what is valid, and the way they stay in agreement is by importing the same
 * function rather than by two people keeping two regexes in step.
 *
 * The database carries the same rule as a CHECK constraint, and THAT copy is
 * authoritative. This one exists to give a person a useful message before the
 * round trip, never to decide. A handle that passes here and fails there is a
 * bug in this file, not a reason to relax the constraint.
 */

/** Matches `profiles_handle_format` in `0001_identity_and_archive.sql`. */
export const HANDLE_PATTERN = /^[a-zA-Z0-9_-]{2,30}$/;
export const HANDLE_MIN = 2;
export const HANDLE_MAX = 30;

export type HandleProblem =
  | { ok: true }
  | { ok: false; reason: string; fix: string };

/**
 * Why this handle is not allowed, in words a person can act on.
 *
 * Returns the FIRST problem rather than all of them, because a form that lists
 * four faults at once is read as a wall and fixed one guess at a time.
 */
export function checkHandle(raw: string): HandleProblem {
  const handle = raw.trim();
  if (handle.length === 0) {
    return { ok: false, reason: 'A handle is required.', fix: 'Pick something 2 to 30 characters.' };
  }
  if (handle.length < HANDLE_MIN) {
    return { ok: false, reason: `"${handle}" is too short.`, fix: `Use at least ${HANDLE_MIN} characters.` };
  }
  if (handle.length > HANDLE_MAX) {
    return { ok: false, reason: `"${handle}" is too long.`, fix: `Use at most ${HANDLE_MAX} characters.` };
  }
  if (!HANDLE_PATTERN.test(handle)) {
    return {
      ok: false,
      reason: `"${handle}" has characters that cannot go in a link.`,
      fix: 'Use letters, numbers, hyphen or underscore.',
    };
  }
  return { ok: true };
}

/**
 * The comparison key, matching the `lower(handle)` unique index.
 *
 * Uniqueness is decided by the database, never by asking it first: a
 * check-then-insert races another registration between the two statements. This
 * exists so a caller can compare two handles it already holds, not so anything
 * can pre-clear one.
 */
export function handleKey(handle: string): string {
  return handle.trim().toLowerCase();
}

/**
 * What to show for a profile with no display name.
 *
 * Null `display_name` is a deterministic fallback and not missing data, so this
 * is the fallback rather than a placeholder waiting to be filled in.
 */
export function displayFor(displayName: string | null, handle: string): string {
  const name = displayName?.trim();
  return name && name.length > 0 ? name : `@${handle}`;
}
